import {
  Mail,
  MessageCircle,
  MessageSquare,
  Paperclip,
  Phone,
} from 'lucide-react';
import React, { useMemo } from 'react';

import { cn } from '@/lib/utils';
import { ClientCommunicationsTabId } from '@/services/types/client-communications-types';

import { useClientCommunications } from './ClientCommunicationsContext';

interface TabConfig {
  id: ClientCommunicationsTabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: TabConfig[] = [
  { id: 'all', label: 'All', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  {
    id: 'texts',
    label: 'Texts',
    icon: <MessageCircle className="h-3.5 w-3.5" />,
  },
  { id: 'calls', label: 'Calls', icon: <Phone className="h-3.5 w-3.5" /> },
  { id: 'emails', label: 'Emails', icon: <Mail className="h-3.5 w-3.5" /> },
  {
    id: 'attachments',
    label: 'Files',
    icon: <Paperclip className="h-3.5 w-3.5" />,
  },
];

export const CommunicationsTabNav: React.FC = () => {
  const { activeTab, setActiveTab, communications, readIds } =
    useClientCommunications();

  // Calculate unread counts for each type (only count inbound messages that haven't been read)
  const unreadCounts = useMemo(() => {
    const counts = {
      sms: 0,
      phone_call: 0,
      email: 0,
    };

    communications.forEach((comm) => {
      // Only count inbound messages as "unread"
      if (comm.direction === 'inbound' && !readIds.has(comm.id)) {
        counts[comm.type] += 1;
      }
    });

    return counts;
  }, [communications, readIds]);

  const getTabCount = (tabId: ClientCommunicationsTabId): number | null => {
    switch (tabId) {
      case 'texts':
        return unreadCounts.sms || null;
      case 'calls':
        return unreadCounts.phone_call || null;
      case 'emails':
        return unreadCounts.email || null;
      default:
        return null;
    }
  };

  return (
    <nav
      className="flex items-center gap-0.5 px-2 pb-0 bg-zinc-50/50"
      aria-label="Communication types"
    >
      {tabs.map((tab) => {
        const count = getTabCount(tab.id);
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors',
              'rounded-t-md border-b-2 -mb-px',
              isActive
                ? 'border-primary text-primary '
                : 'border-transparent text-zinc-600 hover:text-zinc-800',
            )}
          >
            {tab.icon}
            <span>{tab.label}</span>
            {count !== null && count > 0 && (
              <span
                className={cn(
                  'text-[10px] px-1.5 py-px rounded-full tabular-nums leading-tight font-bold',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'bg-zinc-300 text-zinc-600',
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default CommunicationsTabNav;
